import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
// @ts-ignore Ignore unused commented imports
import { CoreActions } from '@next/core-lib';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { ofType, Actions, createEffect } from '@ngrx/effects';
// @ts-ignore Ignore unused commented ActionTypes import
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of, pipe } from 'rxjs';
import {
  mergeMap,
  catchError,
  map,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  exhaustMap,
  tap,
  withLatestFrom,
  startWith,
  finalize,
} from 'rxjs/operators';

import * as fromActions from './customer.actions';

import { getSelectedCustomerId } from './customer.selectors';
import { extractValidationErrors } from '../../shared/helper.functions';
import { Customer, CustomerLocation, SiteState } from '../../shared/models/shared.model';
import { CustomerSearchService } from '../../shared/services/customer-search.service';
import { CustomerService } from '../services/customer.service';

@Injectable()
export class CustomerEffects {
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly customerService = inject(CustomerService);
  private readonly customerSearchService = inject(CustomerSearchService);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject<LocaleService>(LocaleService);

  setCustomerId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.setSelectedCustomerId),
      pipe(
        mergeMap(({ customerId }) =>
          this.customerService.getCustomerById(customerId).pipe(
            map((customer: Customer) => fromActions.setSelectedCustomer({ customer })),
            catchError((error: Error) => of(fromActions.getCustomersError({ error }))),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
        ),
      ),
    ),
  );

  searchCustomers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.searchCustomers),
      debounceTime(400),
      map(({ searchtext }) => (typeof searchtext === 'string' ? searchtext : undefined)),
      distinctUntilChanged(),
      switchMap((searchtext) =>
        searchtext
          ? of(fromActions.getCustomersRequest({ searchtext }))
          : of(fromActions.clearCustomers()),
      ),
    ),
  );

  getCustomersRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getCustomersRequest),
      mergeMap(({ searchtext }) =>
        this.customerSearchService.searchCustomers(searchtext).pipe(
          map((customers: Customer[]) => fromActions.getCustomersSuccess({ customers })),
          catchError((error: Error) => of(fromActions.getCustomersError({ error }))),
        ),
      ),
    ),
  );

  loadCustomersRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadCustomersRequest),
      map(() => fromActions.getCustomersRequest({ searchtext: '' })),
    ),
  );

  upsertCustomerRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.upsertCustomerRequest),
      exhaustMap(({ customer }) =>
        this.customerService.upsertCustomer(customer).pipe(
          map((response: { id: number }) =>
            fromActions.upsertCustomerSuccess({ customer: { ...customer, id: response.id } }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              fromActions.upsertCustomerError({ error }),
              fromActions.loadCustomersRequest(),
              NotificationActions.errorSnackbar({
                message: extractValidationErrors(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  upsertCustomerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.upsertCustomerSuccess),
      map((action) => action.customer),
      withLatestFrom(this.store.select(getSelectedCustomerId)),
      tap(async (_) => this.ngZone.run(() => this.router.navigate([`/customer/`])).then()),
      switchMap((_) =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('app.successmsg.upsert_success'),
          }),
        ),
      ),
    ),
  );

  getLocationsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getLocationsRequest),
      mergeMap(({ customerId }) =>
        this.customerService.getLocations(customerId).pipe(
          map((locations: CustomerLocation[]) => fromActions.getLocationsSuccess({ locations })),
          catchError((error: Error) => of(fromActions.getLocationsError({ error }))),
        ),
      ),
    ),
  );

  getLocationsError$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getLocationsError),
      map((error) =>
        Number(error.error) === 404
          ? this.locale.translate('app.customer.errormsg.not_found')
          : this.locale.translate('app.customer.errormsg.not_loaded'),
      ),
      switchMap((result) => of(NotificationActions.errorSnackbar({ message: result }))),
    ),
  );

  upsertLocationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.upsertLocationRequest),
      exhaustMap(({ customerId, location }) =>
        this.customerService.upsertLocation(customerId, location).pipe(
          map(() => fromActions.upsertLocationSuccess({ location })),
          catchError((error: HttpErrorResponse) =>
            of(
              fromActions.upsertLocationError({ error }),
              fromActions.loadCustomersRequest(),
              NotificationActions.errorSnackbar({ message: extractValidationErrors(error) }),
            ),
          ),
        ),
      ),
    ),
  );

  upsertLocationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.upsertLocationSuccess),
      map((action) => action.location),
      withLatestFrom(this.store.select(getSelectedCustomerId)),
      tap(async ([_, customerId]) =>
        this.ngZone.run(() => this.router.navigate([`/customer/${customerId}/location`])).then(),
      ),
      switchMap((_) =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('app.successmsg.upsert_success'),
          }),
        ),
      ),
    ),
  );

  deleteLocationWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.deleteLocationWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning(this.getDeleteDialogConfimation('location'))
          .pipe(
            switchMap(
              (result) =>
                (result && [fromActions.deleteLocationRequest({ id: action.id })]) || EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteLocationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.deleteLocationRequest),
      mergeMap(({ id }) =>
        this.customerService.deleteLocation(id).pipe(
          map(() => fromActions.deleteLocationSuccess({ id })),
          catchError((error: Error) =>
            of(
              fromActions.deleteLocationError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  private getDeleteDialogConfimation(type: string) {
    return {
      text: this.locale.translate(`app.customer.confirmmsg.delete_item_${type}`),
    };
  }
}
